/**
 * 문서 카탈로그 — 코드 레지스트리 + DB 오버라이드를 합친 결과.
 *
 * 왜 생겼나 (사용자 지적)
 *   Dematic MOU 에 들어가면 "접근 권한이 없습니다" 가 떴다. 원인은 **메뉴 관리에서 그 메뉴를
 *   끈 것**이었다. 문서 접근 판정이 메뉴의 사용/미사용 플래그(menu_role_map.enabled)를 그대로
 *   보고 있었기 때문이다.
 *   문서는 이제 메뉴별로 흩어진 페이지가 아니라 한 화면(/crm/documents/:docKey)에 모인
 *   카탈로그다 — "사이드바에 그 메뉴를 안 띄운다"와 "문서 자체를 못 쓰게 막는다"는 다른
 *   결정이어야 한다. 그 둘을 가르는 게 이 모듈이다.
 *
 * 규칙
 *   1. **코드가 카탈로그, DB 는 오버라이드.** 어떤 문서가 있는지는 documentRegistry 가 정한다.
 *      DB 행이 없거나 조회에 실패해도 목록은 코드 기본값 그대로 나온다 —
 *      문서가 DB 때문에 통째로 사라지는 사고를 막는다(menu_items 와 같은 규칙).
 *   2. **등급(min_role)은 여기 없다.** 이미 menu_role_map 의 같은 키가 갖고 있고 운영 설정이
 *      들어 있다. 저장소를 둘로 만들면 어느 쪽이 이기는지 또 갈린다.
 *      이 모듈이 가져온 건 **사용여부(enabled) 하나**다.
 *   3. 순수 함수다 — 네트워크·React 없이 규칙만 테스트한다.
 */
import {
  DOCUMENT_FAMILY_LABELS,
  DOCUMENT_FAMILY_ORDER,
  documentEntries,
  type DocumentRegistryEntry,
} from "../pages/automation/common/documentRegistry";

/** 서버(/api/auth-users/document-catalog)가 주는 행. 모든 오버라이드 필드는 null 가능 = 코드 값 사용. */
export type DocumentCatalogOverride = {
  doc_key: string;
  label?: string | null;
  family?: string | null;
  sort_order?: number | null;
  /** 마이그레이션 전 서버는 이 필드를 안 줄 수 있다 → 없으면 사용 중으로 본다. */
  enabled?: boolean | null;
  notes?: string | null;
  /** 실행 모듈·바로가기의 화면 경로 오버라이드(20260930 마이그레이션). 없으면 코드 경로. */
  path?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
};

/**
 * 묶음 키. 코드 묶음(DOCUMENT_FAMILY_ORDER + EXECUTION_FAMILY)에 더해 문서 관리에서 슈퍼관리자가
 * 만든 **사용자 묶음**("family-<slug>" 행)도 묶음이다 — 그래서 문자열이다.
 */
export type CatalogFamily = string;

/**
 * 실행 로직 묶음(코드 기본) — 제안서 · 고객 자료 분석 · 3D 시뮬레이션이 기본으로 들어간다.
 * 문서 관리가 Opportunity 단계 "실행 로직" 목록의 정본(마스터)이라, 문서가 아닌 실행 모듈도 여기 있어야 한다.
 */
export const EXECUTION_FAMILY = "execution";
export const EXECUTION_FAMILY_LABEL = "분석 · 제안 · 시뮬레이션";
export const BUILTIN_FAMILY_ORDER: readonly string[] = [...DOCUMENT_FAMILY_ORDER, EXECUTION_FAMILY];
const BUILTIN_FAMILY_LABELS: Record<string, string> = { ...DOCUMENT_FAMILY_LABELS, [EXECUTION_FAMILY]: EXECUTION_FAMILY_LABEL };
export const isBuiltinFamily = (family: string) => BUILTIN_FAMILY_ORDER.includes(family);

/**
 * 레지스트리 문서 중 **실행 모듈**로 쓰는 것 — 제안서. 문서 전환 레일(작성 화면)에는 띄우지 않는다
 * (지금까지처럼). 값 = 플로우 실행 로직 타입(flowLogicConfig 의 FlowLogicType).
 */
export const MODULE_DOC_LOGIC: Readonly<Record<string, string>> = { proposal: "proposal_generation" };
/**
 * 실행 모듈 문서의 기본 경로 — 문서 관리에서 바꿀 수 있다(crm_document_catalog.path).
 * 제안서 = PPTX 제안서 빌더(사용자 요청 2026-10-01: "지금 제안서 페이지 말고 /automation/poc/proposal 과 연결").
 * Opportunity 실행 흐름이 이 경로를 ?projectId=&stepId= 로 연다.
 */
export const MODULE_DOC_PATH: Readonly<Record<string, string>> = { proposal: "/automation/poc/proposal" };

/**
 * 문서가 아닌 실행 모듈 — 편집 폼이 없어 레지스트리에 넣지 않는다(레지스트리 = 작성 화면이 있는 문서).
 * 이름 · 묶음 · 순서 · 사용 여부 · **경로**를 문서 관리가 정한다. 같은 테이블(crm_document_catalog)의 같은 키 규칙.
 * 3D 시뮬레이션 기본 경로 = 디지털 트윈 스튜디오(사용자 요청 — 2026-09-30). 옛 샌드박스(/crm/3d-simulation)는 라우트만 남는다.
 */
export type CatalogModuleDef = { key: string; label: string; logicType: string; path: string };
export const CATALOG_MODULES: readonly CatalogModuleDef[] = [
  { key: "customer-data-analysis", label: "고객 자료 분석", logicType: "customer_data_analysis", path: "/crm/logistics-data-lab" },
  { key: "simulation-3d", label: "3D 시뮬레이션", logicType: "simulation_3d", path: "/automation/poc/twin-studio" },
  // 단계 산출물로 걸 수 있는 바로가기 모듈(2026-10-14) — 자동 생성은 없고 Opportunity 실행 로직에서 열기만 한다.
  //   회의록은 Opportunity 화면 안 회의록 창으로 열고(경로는 단독 화면용), ROI 는 이 경로를 ?projectId= 로 연다.
  { key: "meeting-notes", label: "회의록", logicType: "meeting_note", path: "/automation/poc/meeting-notes" },
  { key: "roi-analysis", label: "ROI 분석", logicType: "roi_analysis", path: "/planning/roi-analysis" },
];

/** 실행 모듈 한 줄(문서 관리 표·플로우 실행 로직 공용). */
export type CatalogModuleItem = {
  key: string;
  label: string;
  codeLabel: string;
  family?: CatalogFamily;
  codeFamily?: CatalogFamily;
  sortOrder: number;
  enabled: boolean;
  overridden: boolean;
  logicType: string;
  /** 합쳐진 경로(오버라이드 → 코드) */
  path: string;
  codePath: string;
};

/**
 * 바로가기 — 문서 관리에서 **추가·삭제**할 수 있는 항목. 코드 없이 이름 + 경로만으로 만든다.
 *   doc_key = "link-<slug>" 행. 행이 있으면 있는 것, 행을 지우면 없어진다(완전 삭제).
 *   Opportunity 실행 로직 목록에 같은 묶음·순서로 뜨고, 누르면 그 경로를 새 탭으로 연다(단계 산출물·상태는 없다).
 * ★ 새 "문서"(작성 폼)는 여전히 코드가 필요하다 — 바로가기는 이미 있는 화면으로 가는 링크다.
 */
export const CATALOG_LINK_PREFIX = "link-";
export const isCatalogLinkKey = (key: string) => key.startsWith(CATALOG_LINK_PREFIX);
export type CatalogLinkItem = {
  key: string;
  label: string;
  family?: CatalogFamily;
  sortOrder: number;
  enabled: boolean;
  path: string;
};

/** 경로 최대 길이 — 서버(api/auth-users-handler.js MODULE_PATH_MAX)와 같다. */
export const MODULE_PATH_MAX = 300;

/**
 * 입력한 경로를 저장할 모양으로 — 앱 안 경로("/"로 시작)만 받는다. 같은 주소(origin)의 전체 URL을 붙여 넣으면
 * 경로만 남긴다(예: https://app.vault.colosseum.global/automation/poc/twin-studio → /automation/poc/twin-studio).
 * 받을 수 없으면 null — 외부 주소 · "//" · 공백 · 역슬래시 · 너무 긴 값.
 */
export function normalizeModulePath(input: string, origin?: string): string | null {
  let value = String(input ?? "").trim();
  if (!value) return null;
  const sameOrigins = [origin, "https://app.vault.colosseum.global"].filter(Boolean) as string[];
  for (const o of sameOrigins) {
    if (value.toLowerCase().startsWith(o.toLowerCase())) {
      value = value.slice(o.length) || "/";
      break;
    }
  }
  if (!isInternalPath(value)) return null;
  return value;
}

/** 서버와 같은 규칙 — "/"로 시작, "//" 금지(다른 호스트로 가는 주소), 공백·역슬래시 금지. */
export function isInternalPath(value: unknown): value is string {
  return typeof value === "string" && value.length <= MODULE_PATH_MAX && /^\/(?![\/\\])[^\s\\]*$/.test(value);
}

/**
 * 실행 모듈 화면을 Opportunity 에서 열 때 — 어느 기회·단계에서 왔는지 붙인다
 * (트윈 스튜디오는 ?projectId= 로 기회를, ?stepId= 로 단계를 고른다). 경로에 이미 있는 값은 두고 없는 것만 붙인다.
 */
export function modulePathWithContext(path: string, context: { projectId?: string | null; stepId?: string | null }): string {
  const [beforeHash, hash = ""] = path.split("#");
  const [pathname, query = ""] = beforeHash.split("?");
  const params = new URLSearchParams(query);
  if (context.projectId && !params.has("projectId")) params.set("projectId", context.projectId);
  if (context.stepId && !params.has("stepId")) params.set("stepId", context.stepId);
  const q = params.toString();
  return `${pathname}${q ? `?${q}` : ""}${hash ? `#${hash}` : ""}`;
}

/** 화면이 쓰는 한 줄. 코드 값과 오버라이드를 합친 **결과**와, 어디서 왔는지(overridden)를 같이 준다. */
export type DocumentCatalogItem = {
  key: string;
  /** 합쳐진 표시 이름 */
  label: string;
  /** 코드가 정한 이름 — 관리 화면이 "기본값으로 되돌리기"를 보여줄 때 쓴다 */
  codeLabel: string;
  family?: CatalogFamily;
  codeFamily?: CatalogFamily;
  /** 정렬 키. 오버라이드가 없으면 코드 순서(레지스트리 인덱스)다. */
  sortOrder: number;
  enabled: boolean;
  notes: string;
  /** 통합 작성 화면(/crm/documents/<key>)에 올라간 문서인가 */
  workspace: boolean;
  /** 이 문서에 DB 오버라이드 행이 있는가 — 관리 화면의 "기본값으로" 버튼 노출 조건 */
  overridden: boolean;
  updatedBy: string;
  /** 실행 모듈 문서(제안서)의 열 경로 · 코드 기본 경로 — 다른 문서는 없음 */
  path?: string;
  codePath?: string;
  updatedAt: string;
  /** 코드 원본 — 경로·실행 메타가 필요한 소비처용 */
  entry: DocumentRegistryEntry;
};

/** 오버라이드에 있는 사용자 묶음 키들("family-<slug>" 행 중 코드 묶음이 아닌 것). */
function customFamiliesOf(overrides: readonly DocumentCatalogOverride[]): Set<string> {
  const out = new Set<string>();
  overrides.forEach((row) => {
    const key = row && typeof row.doc_key === "string" ? row.doc_key : "";
    if (!key.startsWith(DOCUMENT_FAMILY_ROW_PREFIX)) return;
    const family = key.slice(DOCUMENT_FAMILY_ROW_PREFIX.length);
    if (family && !isBuiltinFamily(family)) out.add(family);
  });
  return out;
}

function isFamily(value: unknown, custom: Set<string>): value is CatalogFamily {
  return typeof value === "string" && (isBuiltinFamily(value) || custom.has(value));
}

/**
 * 묶음(상위) 행의 이름·순서 오버라이드도 **같은 테이블**(crm_document_catalog)에 둔다.
 *   doc_key = "family-<묶음키>" · label = 묶음 이름 · sort_order = 묶음 순서.
 * 새 테이블·마이그레이션 없이 문서 관리 화면을 메뉴 관리처럼 "상위·하위" 트리로 만들기 위해서다.
 * 레지스트리에 없는 키라 mergeDocumentCatalog 는 이 행을 문서로 보지 않는다(코드가 카탈로그).
 * ★ 문서 키는 "family-" 로 시작하면 안 된다 — 테스트가 막는다.
 */
export const DOCUMENT_FAMILY_ROW_PREFIX = "family-";
export const documentFamilyRowKey = (family: CatalogFamily): string => `${DOCUMENT_FAMILY_ROW_PREFIX}${family}`;

/** 묶음 한 줄 — 코드 이름·순서에 오버라이드를 얹은 결과. */
export type DocumentFamilyItem = {
  family: CatalogFamily;
  /** 코드 묶음인가 — 코드 묶음은 지울 수 없다(이름·순서만 바꾼다). 사용자 묶음은 비었을 때 지울 수 있다. */
  builtin: boolean;
  /** crm_document_catalog 의 doc_key */
  rowKey: string;
  label: string;
  codeLabel: string;
  sortOrder: number;
  overridden: boolean;
};

/** 묶음 목록 — 순서 오버라이드가 없으면 코드 순서(DOCUMENT_FAMILY_ORDER). */
export function mergeDocumentFamilies(overrides: readonly DocumentCatalogOverride[]): DocumentFamilyItem[] {
  const byKey = new Map<string, DocumentCatalogOverride>();
  overrides.forEach((row) => {
    if (row && typeof row.doc_key === "string") byKey.set(row.doc_key, row);
  });
  const orderOf = (row: DocumentCatalogOverride | undefined) =>
    row?.sort_order !== null && row?.sort_order !== undefined && Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : null;
  const builtins = BUILTIN_FAMILY_ORDER.map((family, index) => {
    const rowKey = documentFamilyRowKey(family);
    const row = byKey.get(rowKey);
    const label = typeof row?.label === "string" && row.label.trim() ? row.label.trim() : null;
    return {
      family,
      builtin: true,
      rowKey,
      label: label ?? BUILTIN_FAMILY_LABELS[family],
      codeLabel: BUILTIN_FAMILY_LABELS[family],
      sortOrder: orderOf(row) ?? index,
      overridden: Boolean(row),
    };
  });
  // 사용자 묶음 — 문서 관리에서 만든 "family-<slug>" 행. 코드 이름이 없으니 저장된 이름이 곧 이름이다.
  const custom = Array.from(customFamiliesOf(overrides)).map((family, index) => {
    const rowKey = documentFamilyRowKey(family);
    const row = byKey.get(rowKey);
    const label = typeof row?.label === "string" && row.label.trim() ? row.label.trim() : family;
    return { family, builtin: false, rowKey, label, codeLabel: label, sortOrder: orderOf(row) ?? BUILTIN_FAMILY_ORDER.length + index, overridden: true };
  });
  return [...builtins, ...custom].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * 코드 레지스트리에 DB 오버라이드를 얹는다.
 *
 * ★ 목록의 출발점은 **언제나 코드**다. DB 에만 있는 doc_key 는 무시한다 —
 *   편집 화면이 없는 문서를 목록에 띄우면 눌렀을 때 "문서를 찾을 수 없습니다" 가 된다.
 */
export function mergeDocumentCatalog(
  overrides: readonly DocumentCatalogOverride[],
  entries: readonly DocumentRegistryEntry[] = documentEntries()
): DocumentCatalogItem[] {
  const byKey = new Map<string, DocumentCatalogOverride>();
  overrides.forEach((row) => {
    if (row && typeof row.doc_key === "string") byKey.set(row.doc_key, row);
  });
  const custom = customFamiliesOf(overrides);

  return entries
    .map((entry, index) => {
      const row = byKey.get(entry.key);
      const overrideLabel = typeof row?.label === "string" && row.label.trim() ? row.label.trim() : null;
      const overrideFamily = isFamily(row?.family, custom) ? row!.family as CatalogFamily : undefined;
      // 실행 모듈로 쓰는 문서(제안서)는 코드 묶음이 없으면 "실행 로직" 묶음이 기본이다.
      const codeFamily: CatalogFamily | undefined = entry.family ?? (MODULE_DOC_LOGIC[entry.key] ? EXECUTION_FAMILY : undefined);
      const overrideOrder =
        row?.sort_order !== null && row?.sort_order !== undefined && Number.isFinite(Number(row.sort_order))
          ? Number(row.sort_order)
          : null;

      return {
        key: entry.key as string,
        label: overrideLabel ?? entry.label,
        codeLabel: entry.label,
        family: overrideFamily ?? codeFamily,
        codeFamily,
        // 코드 순서는 인덱스 그대로다 — 오버라이드가 없는 문서끼리는 레지스트리 순서를 지킨다.
        sortOrder: overrideOrder ?? index,
        // enabled 가 없으면(행 없음 / 마이그레이션 전 서버) 사용 중이다.
        // 기능이 조용히 꺼지는 쪽이 켜져 있는 쪽보다 훨씬 나쁘다.
        enabled: row?.enabled !== false,
        notes: typeof row?.notes === "string" ? row.notes : "",
        workspace: entry.workspace === true,
        overridden: Boolean(row),
        updatedBy: typeof row?.updated_by === "string" ? row.updated_by : "",
        updatedAt: typeof row?.updated_at === "string" ? row.updated_at : "",
        // 실행 모듈 문서(제안서)만 경로를 가진다 — 문서 관리 값이 있으면 그것, 없으면 코드 기본
        ...(MODULE_DOC_PATH[entry.key] ? { path: isInternalPath(row?.path) ? row!.path as string : MODULE_DOC_PATH[entry.key], codePath: MODULE_DOC_PATH[entry.key] } : {}),
        entry,
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * 실행 모듈(고객 자료 분석 · 3D) — 문서와 같은 오버라이드 규칙. 기본 묶음 = 실행 로직, 기본 순서 =
 * 제안서 앞뒤(체인 순서: 고객 자료 분석 → 제안서 → 3D).
 */
export function mergeModuleCatalog(
  overrides: readonly DocumentCatalogOverride[],
  entries: readonly DocumentRegistryEntry[] = documentEntries()
): CatalogModuleItem[] {
  const byKey = new Map<string, DocumentCatalogOverride>();
  overrides.forEach((row) => {
    if (row && typeof row.doc_key === "string") byKey.set(row.doc_key, row);
  });
  const custom = customFamiliesOf(overrides);
  const proposalIndex = Math.max(0, entries.findIndex((e) => e.key === "proposal"));
  // 파이프라인 순서: 회의록 → 고객 자료 분석 → ROI → 제안서 → 3D
  const codeOrder: Record<string, number> = { "meeting-notes": proposalIndex - 0.9, "customer-data-analysis": proposalIndex - 0.5, "roi-analysis": proposalIndex - 0.3, "simulation-3d": proposalIndex + 0.5 };
  return CATALOG_MODULES.map((m, i) => {
    const row = byKey.get(m.key);
    const label = typeof row?.label === "string" && row.label.trim() ? row.label.trim() : null;
    const order = row?.sort_order !== null && row?.sort_order !== undefined && Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : null;
    return {
      key: m.key,
      label: label ?? m.label,
      codeLabel: m.label,
      family: isFamily(row?.family, custom) ? (row!.family as CatalogFamily) : EXECUTION_FAMILY,
      codeFamily: EXECUTION_FAMILY,
      sortOrder: order ?? codeOrder[m.key] ?? proposalIndex + 1 + i,
      enabled: row?.enabled !== false,
      overridden: Boolean(row),
      logicType: m.logicType,
      path: isInternalPath(row?.path) ? row!.path as string : m.path,
      codePath: m.path,
    };
  }).sort((a, b) => a.sortOrder - b.sortOrder);
}

/** 바로가기("link-<slug>" 행) — 경로가 올바른 행만. 묶음을 모르면(지워진 사용자 묶음) 실행 로직 묶음으로. */
export function mergeLinkCatalog(overrides: readonly DocumentCatalogOverride[]): CatalogLinkItem[] {
  const custom = customFamiliesOf(overrides);
  return overrides
    .filter((row) => row && typeof row.doc_key === "string" && isCatalogLinkKey(row.doc_key) && isInternalPath(row.path))
    .map((row, index) => ({
      key: row.doc_key,
      label: typeof row.label === "string" && row.label.trim() ? row.label.trim() : row.doc_key,
      family: isFamily(row.family, custom) ? (row.family as CatalogFamily) : EXECUTION_FAMILY,
      sortOrder: row.sort_order !== null && row.sort_order !== undefined && Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : 1000 + index,
      enabled: row.enabled !== false,
      path: row.path as string,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** 레일·플로우빌더 등 "쓸 수 있는 문서"만 필요한 소비처용. */
export function enabledDocuments(items: readonly DocumentCatalogItem[]): DocumentCatalogItem[] {
  return items.filter((item) => item.enabled);
}

/**
 * 문서 전환 레일의 묶음. documentFamilyGroups() 와 같은 모양이지만
 * **카탈로그를 거친다** — 관리 화면에서 끈 문서·바꾼 이름·바꾼 순서가 그대로 반영된다.
 */
export function catalogFamilyGroups(
  items: readonly DocumentCatalogItem[],
  families: readonly DocumentFamilyItem[] = mergeDocumentFamilies([])
): { family: CatalogFamily; label: string; items: DocumentCatalogItem[] }[] {
  // 묶음 이름·순서도 문서 관리에서 바꾼 값을 따른다. 실행 모듈로 쓰는 문서(제안서)는 작성 레일에 넣지 않는다.
  return families.map(({ family, label }) => ({
    family,
    label,
    items: items.filter((item) => item.family === family && !MODULE_DOC_LOGIC[item.key]),
  })).filter((group) => group.items.length > 0);
}

/**
 * 이 문서를 쓸 수 있는가 — **사용여부만** 본다.
 * 등급 판정은 호출측이 menu_role_map 으로 따로 한다(이 모듈은 등급을 모른다).
 *
 * ★ 코드에 없는 키는 true 를 돌려준다. 카탈로그가 모르는 문서를 여기서 막으면,
 *   레지스트리에 아직 안 올라간 화면이 원인 모를 "접근 권한 없음"이 된다 —
 *   지금 고치고 있는 바로 그 증상이다.
 */
export function isDocumentEnabled(items: readonly DocumentCatalogItem[], docKey: string): boolean {
  const found = items.find((item) => item.key === docKey);
  return found ? found.enabled : true;
}
